import { api } from '../api';
import { isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatCurrency, formatDate, statusBadge, icons } from '../helpers';
import { toast, openModal, closeModal, confirmDialog, setBreadcrumb, skeletonTable, makeSortable, bindSearch } from '../ui';

export async function renderClients() {
  if (!isAdmin()) return navigate('/dashboard');
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Clients' }]);
  setHTML(content, skeletonTable(6, 5));

  try {
    const data = await api.get('/clients');
    const clients = data.clients || [];
    let rows = '';
    clients.forEach((c: any) => {
      rows += `<tr>
        <td><a href="/clients/${c.id}" class="table-link">${esc(c.name)}</a><br><span class="text-muted" style="font-size:0.8rem">${esc(c.email)}</span></td>
        <td>${esc(c.company || '\u2014')}</td>
        <td>${esc(c.country || '\u2014')}</td>
        <td>${esc(c.project_count || 0)}</td>
        <td>${formatCurrency(c.total_paid)}</td>
        <td class="row-actions">
          <button class="btn btn-ghost btn-sm" data-edit-client="${c.id}"><i data-lucide="pencil"></i></button>
          <button class="btn btn-ghost btn-sm" data-deactivate-client="${c.id}" title="Deactivate"><i data-lucide="user-x"></i></button>
        </td>
      </tr>`;
    });

    const tableHtml = !clients.length
      ? '<div class="empty-state"><i data-lucide="users"></i><p>No clients yet</p></div>'
      : `<div class="table-wrapper"><table id="clients-table"><thead><tr>
          <th data-sort="name">Client</th><th data-sort="company">Company</th><th data-sort="country">Country</th>
          <th data-sort="projects">Projects</th><th data-sort="revenue">Revenue</th><th>Actions</th>
        </tr></thead><tbody>${rows}</tbody></table></div>`;

    // Check for pending approvals
    let pendingHtml = '';
    try {
      const pendingData = await api.get('/clients/pending');
      const pending = pendingData.pending || [];
      if (pending.length > 0) {
        let pRows = '';
        pending.forEach((p: any) => {
          pRows += `<tr>
            <td>${esc(p.name)}<br><span class="text-muted" style="font-size:0.8rem">${esc(p.email)}</span></td>
            <td>${esc(p.company || '\u2014')}</td>
            <td>${statusBadge(p.account_type)}</td>
            <td>${esc(p.country || '\u2014')}</td>
            <td>${formatDate(p.created_at)}</td>
            <td class="row-actions" style="white-space:nowrap">
              <button class="btn btn-primary btn-sm" data-approve="${p.id}"><i data-lucide="check"></i> Approve</button>
              <button class="btn btn-danger btn-sm" data-reject="${p.id}"><i data-lucide="x"></i> Reject</button>
            </td>
          </tr>`;
        });
        pendingHtml = `<div class="card mb-2" style="border-left:3px solid var(--warning)" id="pending-card">
          <div class="card-header"><h3><i data-lucide="user-check" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem"></i> Pending Approvals (${pending.length})</h3></div>
          <div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr>
            <th>Applicant</th><th>Company</th><th>Type</th><th>Country</th><th>Applied</th><th>Actions</th>
          </tr></thead><tbody>${pRows}</tbody></table></div></div></div>`;
      }
    } catch { /* ignore */ }

    setHTML(content, `<div class="page-enter">
      <div class="page-header"><h1>Clients</h1><div class="page-header-actions">
        <div class="search-input-wrapper"><i data-lucide="search"></i><input type="text" id="clients-search" placeholder="Search clients..."></div>
        <button class="btn btn-primary" id="add-client-btn"><i data-lucide="user-plus"></i> Add Client</button>
      </div></div>
      ${pendingHtml}
      <div class="card"><div class="card-body no-pad" id="clients-container">${tableHtml}</div></div>
    </div>`);

    icons();
    const table = document.getElementById('clients-table');
    if (table) makeSortable(table);
    bindSearch('clients-search', 'tr', '#clients-table tbody');

    document.getElementById('add-client-btn')?.addEventListener('click', () => openClientForm());
    $$('[data-edit-client]').forEach(el => {
      el.addEventListener('click', (e) => { e.stopPropagation(); editClient(parseInt(el.getAttribute('data-edit-client')!)); });
    });
    $$('[data-deactivate-client]').forEach(el => {
      el.addEventListener('click', async (e) => {
        e.stopPropagation();
        const cid = el.getAttribute('data-deactivate-client');
        if (await confirmDialog('Deactivate Client', 'Deactivate this client? They will be hidden from lists but their data is kept and can be restored.', 'Deactivate', 'danger')) {
          try { await api.del('/clients/' + cid); toast('Client deactivated', 'success'); renderClients(); }
          catch (err: any) { toast(err.message, 'error'); }
        }
      });
    });

    // Pending approval actions
    $$('[data-approve]').forEach(el => {
      el.addEventListener('click', async (e) => {
        e.stopPropagation();
        const uid = el.getAttribute('data-approve');
        try {
          await api.post('/clients/' + uid + '/approve', {});
          toast('Account approved', 'success');
          renderClients();
        } catch (err: any) { toast(err.message, 'error'); }
      });
    });
    $$('[data-reject]').forEach(el => {
      el.addEventListener('click', async (e) => {
        e.stopPropagation();
        const uid = el.getAttribute('data-reject');
        if (await confirmDialog('Reject Registration', 'Are you sure you want to reject this registration? The account will be permanently deleted.', 'Reject', 'danger')) {
          try {
            await api.post('/clients/' + uid + '/reject', {});
            toast('Registration rejected', 'success');
            renderClients();
          } catch (err: any) { toast(err.message, 'error'); }
        }
      });
    });
  } catch (err: any) { toast(err.message, 'error'); }
}

export async function renderClientDetail(id: number) {
  if (!isAdmin()) return navigate('/dashboard');
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Clients', href: '/clients' }, { label: 'Loading...' }]);

  try {
    const [clientData, projectsData, invoicesData] = await Promise.all([
      api.get('/clients/' + id),
      api.get('/projects'),
      api.get('/invoices'),
    ]);
    const c = clientData.client;
    const projects = (projectsData.projects || []).filter((p: any) => p.client_id === id);
    const invoices = (invoicesData.invoices || []).filter((i: any) => i.client_id === id);

    setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Clients', href: '/clients' }, { label: c.name }]);

    let projectRows = '';
    projects.forEach((p: any) => {
      projectRows += `<tr><td><a href="/projects/${p.id}" class="table-link">${esc(p.title)}</a></td><td>${statusBadge(p.status)}</td><td>${formatCurrency(p.budget, p.currency)}</td><td>${formatDate(p.deadline)}</td></tr>`;
    });
    let invoiceRows = '';
    invoices.forEach((i: any) => {
      invoiceRows += `<tr><td><a href="/invoices/${i.id}" class="table-link font-mono">${esc(i.invoice_number)}</a></td><td>${statusBadge(i.status)}</td><td>${formatCurrency(i.total, i.currency)}</td><td>${formatDate(i.issue_date)}</td></tr>`;
    });

    setHTML(content, `<div class="page-enter">
      <div class="page-header"><h1>${esc(c.name)}</h1><div class="page-header-actions">
        <button class="btn btn-secondary" id="edit-client-btn"><i data-lucide="pencil"></i> Edit</button>
        <button class="btn btn-secondary" id="delete-client-btn"><i data-lucide="user-x"></i> Deactivate</button>
        <button class="btn btn-danger" id="permadelete-client-btn"><i data-lucide="trash-2"></i> Delete permanently</button>
      </div></div>
      <div class="card mb-2"><div class="card-body"><div class="detail-grid">
        <div class="detail-item"><div class="detail-label">Email</div><div class="detail-value">${esc(c.email)}</div></div>
        <div class="detail-item"><div class="detail-label">Company</div><div class="detail-value">${esc(c.company || '\u2014')}</div></div>
        <div class="detail-item"><div class="detail-label">Phone</div><div class="detail-value">${esc(c.phone || '\u2014')}</div></div>
        <div class="detail-item"><div class="detail-label">Country</div><div class="detail-value">${esc(c.country || '\u2014')}</div></div>
        <div class="detail-item"><div class="detail-label">Tax ID</div><div class="detail-value">${esc(c.tax_id || c.vat_number || '\u2014')}</div></div>
        <div class="detail-item"><div class="detail-label">Status</div><div class="detail-value">${c.is_active ? statusBadge('active') : statusBadge('cancelled')}</div></div>
        <div class="detail-item detail-full"><div class="detail-label">Address</div><div class="detail-value">${esc(c.address || '\u2014')}</div></div>
        <div class="detail-item"><div class="detail-label">Last Login</div><div class="detail-value">${formatDate(c.last_login)}</div></div>
        <div class="detail-item"><div class="detail-label">Created</div><div class="detail-value">${formatDate(c.created_at)}</div></div>
      </div></div></div>
      <div class="card mb-2"><div class="card-header"><h3>Projects (${projects.length})</h3></div><div class="card-body no-pad">
        ${projects.length ? `<div class="table-wrapper"><table><thead><tr><th>Title</th><th>Status</th><th>Budget</th><th>Deadline</th></tr></thead><tbody>${projectRows}</tbody></table></div>` : '<div class="empty-state" style="padding:1.5rem"><p>No projects</p></div>'}
      </div></div>
      <div class="card"><div class="card-header"><h3>Invoices (${invoices.length})</h3></div><div class="card-body no-pad">
        ${invoices.length ? `<div class="table-wrapper"><table><thead><tr><th>Invoice</th><th>Status</th><th>Total</th><th>Date</th></tr></thead><tbody>${invoiceRows}</tbody></table></div>` : '<div class="empty-state" style="padding:1.5rem"><p>No invoices</p></div>'}
      </div></div>
    </div>`);

    icons();
    document.getElementById('edit-client-btn')?.addEventListener('click', () => editClient(id));
    document.getElementById('delete-client-btn')?.addEventListener('click', async () => {
      if (await confirmDialog('Deactivate Client', 'Are you sure you want to deactivate this client?', 'Deactivate', 'danger')) {
        await api.del('/clients/' + id);
        toast('Client deactivated', 'success');
        navigate('/clients');
      }
    });
    document.getElementById('permadelete-client-btn')?.addEventListener('click', async () => {
      if (await confirmDialog('Permanently Delete Client', 'This permanently deletes the client AND all of their projects, invoices, payments, documents, tickets and messages. This cannot be undone. Continue?', 'Delete permanently', 'danger')) {
        try {
          await api.del('/clients/' + id + '/permanent');
          toast('Client permanently deleted', 'success');
          navigate('/clients');
        } catch (err: any) { toast(err.message, 'error'); }
      }
    });
  } catch (err: any) { toast(err.message, 'error'); }
}

export function openClientForm(client?: any) {
  const isEdit = !!client;
  openModal(isEdit ? 'Edit Client' : 'New Client',
    `<form id="client-form">
      <div class="form-row"><div class="form-group"><label>Full Name *</label><input type="text" name="name" value="${esc(client?.name || '')}" required></div>
      <div class="form-group"><label>Email *</label><input type="email" name="email" value="${esc(client?.email || '')}" required ${isEdit ? 'disabled' : ''}></div></div>
      <div class="form-row"><div class="form-group"><label>Company</label><input type="text" name="company" value="${esc(client?.company || '')}"></div>
      <div class="form-group"><label>Phone</label><input type="text" name="phone" value="${esc(client?.phone || '')}"></div></div>
      <div class="form-row"><div class="form-group"><label>Country</label><input type="text" name="country" value="${esc(client?.country || '')}"></div>
      <div class="form-group"><label>Tax ID</label><input type="text" name="tax_id" value="${esc(client?.tax_id || '')}"></div></div>
      <div class="form-group"><label>Address</label><textarea name="address" rows="2">${esc(client?.address || '')}</textarea></div>
      ${isEdit ? '<div class="form-group"><label>New Password (leave blank to keep)</label><input type="password" name="password" minlength="8"></div>'
        : '<div class="form-group"><label>Password *</label><input type="password" name="password" minlength="8" required placeholder="Min 8 characters"></div>'}
      <div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="client-cancel">Cancel</button>
      <button type="submit" class="btn btn-primary">${isEdit ? 'Save Changes' : 'Create Client'}</button></div></form>`);

  document.getElementById('client-cancel')?.addEventListener('click', closeModal);
  document.getElementById('client-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    if (!body.password) delete body.password;
    try {
      if (isEdit) { await api.put('/clients/' + client.id, body); toast('Client updated', 'success'); }
      else { await api.post('/clients', body); toast('Client created', 'success'); }
      closeModal(); navigate('/clients');
    } catch (err: any) { toast(err.message, 'error'); }
  });
}

async function editClient(id: number) {
  try {
    const data = await api.get('/clients/' + id);
    openClientForm(data.client);
  } catch (err: any) { toast(err.message, 'error'); }
}
